import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useLanguage } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Shield, 
  LayoutDashboard, 
  FileText, 
  Bell, 
  BarChart3, 
  MessageSquare, 
  Settings, 
  Globe,
  LogOut,
  User,
  Menu,
  X,
  Info,
  Users,
  ShieldCheck,
  FileWarning,
  Calendar
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('polibot_token');
        if (token) {
          const response = await axios.get(`${API}/admin/check`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(response.data.is_admin);
        }
      } catch (error) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [user]);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/policies', icon: FileText, label: t('nav.policies') },
    { path: '/deed-theft', icon: FileWarning, label: language === 'es' ? 'Robo de Escrituras' : language === 'ar' ? 'سرقة الصكوك' : language === 'ht' ? 'Vòl Tit' : 'Deed Theft' },
    { path: '/meetings', icon: Calendar, label: language === 'es' ? 'Reuniones' : language === 'ar' ? 'اجتماعات' : language === 'ht' ? 'Reyinyon' : 'Meetings' },
    { path: '/alerts', icon: Bell, label: t('nav.alerts') },
    { path: '/risk', icon: BarChart3, label: t('nav.risk') },
    { path: '/comments', icon: MessageSquare, label: t('nav.comments') },
    { path: '/community', icon: Users, label: language === 'es' ? 'Comunidad' : language === 'ar' ? 'مجتمع' : language === 'ht' ? 'Kominote' : 'Community' },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
    // Admin link - only shown if user is admin
    ...(isAdmin ? [{ path: '/admin', icon: ShieldCheck, label: 'Admin' }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-border/50 h-16">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 hover:bg-muted rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            <Link to="/dashboard" className="flex items-center gap-2">
              <Shield className="h-7 w-7 text-primary" />
              <div className="hidden sm:block">
                <span className="font-bold text-lg tracking-tight leading-none">PoliBot</span>
                <p className="text-[9px] text-muted-foreground leading-none">NYC Environmental Justice</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
                  data-testid="language-toggle"
                >
                  <Globe className="h-4 w-4" />
                  {language.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage('en')} className={language === 'en' ? 'bg-muted' : ''}>
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('es')} className={language === 'es' ? 'bg-muted' : ''}>
                  Español
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('ar')} className={language === 'ar' ? 'bg-muted' : ''}>
                  العربية
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('ht')} className={language === 'ht' ? 'bg-muted' : ''}>
                  Kreyòl Ayisyen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <span className="hidden sm:block text-sm font-medium truncate max-w-[150px]">
                    {user?.company_name || user?.organization_name || 'User'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.company_name || user?.organization_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {user?.user_type === 'corporate_esg' && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/corporate')} data-testid="menu-corporate">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Portal Corporativo' : language === 'fr' ? 'Portail Entreprise' : language === 'ar' ? 'بوابة الشركات' : language === 'zh' ? '企業門戶' : 'Corporate Portal'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('/')} data-testid="menu-about">
                  <Info className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Acerca de PoliBot' : language === 'fr' ? 'À propos' : language === 'ar' ? 'حول' : language === 'zh' ? '關於' : 'About PoliBot'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <nav className="fixed top-16 left-0 bottom-0 w-64 bg-background border-r border-border p-4 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid={`mobile-nav-${item.path.slice(1)}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
